import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { useAquariums } from '../../hooks/useAquariums';
import { latestMeasurementsKey } from '../../hooks/useLatestMeasurements';
import { getLatestMeasurements } from '../../services/measurementService';
import { getLatestProbeValues } from '../../api/controller';
import { controllerKeys } from '../../hooks/useController';
import {
  USER_PREFERENCES_QUERY_KEY,
  fetchUserPreferences,
  updateUserPreferences,
  type UserPreferences,
} from '../../services/preferencesService';
import { PARAMETER_OPTIONS } from '../../types/measurement';
import { parameterLabel } from '../measurements/measurementHistoryUtils';
import { DASHBOARD_PARAMETERS_MAX, DEFAULT_DASHBOARD_PARAMETERS } from '../dashboard/dashboardCards';
import './DashboardCardsSection.css';

/** Anchor of this section — the dashboard's "Choose cards" button links here. */
export const DASHBOARD_CARDS_ANCHOR = 'dashboard-cards';

export interface DashboardCardsSectionProps {
  className?: string;
}

interface Option {
  id: string;
  label: string;
}

/**
 * Settings → "Dashboard": which measurement cards the dashboard shows, up to
 * DASHBOARD_PARAMETERS_MAX, in the order they were ticked. Offers every hand-test
 * parameter plus every other value the user's aquariums have (ICP elements,
 * controller probes such as redox or oxygen, …). Each change is saved at once (PATCH /api/v1/me/preferences);
 * "Reset" goes back to the default set.
 */
export function DashboardCardsSection({ className = '' }: DashboardCardsSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const prefs = useQuery({ queryKey: USER_PREFERENCES_QUERY_KEY, queryFn: fetchUserPreferences, staleTime: 5 * 60 * 1000, retry: false });
  const { data: aquariums } = useAquariums();
  // The values of every aquarium (same query as the dashboard and the latest-values table).
  const latest = useQueries({
    queries: (aquariums ?? []).map((a) => ({
      queryKey: latestMeasurementsKey(a.id),
      queryFn: () => getLatestMeasurements(a.id),
      staleTime: 5 * 60 * 1000,
    })),
  });
  // Values only a controller probe measures (redox, oxygen, …) can be cards too (#1899).
  const probes = useQueries({
    queries: (aquariums ?? []).map((a) => ({
      queryKey: controllerKeys.probeValues(a.id),
      queryFn: () => getLatestProbeValues(a.id),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<'saved' | 'reset' | null>(null);
  const [failed, setFailed] = useState(false);

  const loaded = prefs.data !== undefined;
  useEffect(() => {
    if (!loaded || typeof window === 'undefined' || window.location.hash !== `#${DASHBOARD_CARDS_ANCHOR}`) return;
    document.getElementById(DASHBOARD_CARDS_ANCHOR)?.scrollIntoView?.({ block: 'start' });
  }, [loaded]);

  const selected = useMemo(
    () => prefs.data?.dashboardParameters ?? [...DEFAULT_DASHBOARD_PARAMETERS],
    [prefs.data],
  );

  // Cheap enough to rebuild on every render (a few dozen parameters at most).
  const testIds = new Set<string>(PARAMETER_OPTIONS.map((p) => p.id));
  const tests: Option[] = PARAMETER_OPTIONS.map((p) => ({ id: p.id, label: parameterLabel(t, p.id, p.name) }));
  const others = new Map<string, Option>();
  for (const p of latest.flatMap((q) => q.data ?? [])) {
    if (!testIds.has(p.parameterId)) others.set(p.parameterId, { id: p.parameterId, label: parameterLabel(t, p.parameterId, p.parameterName) });
  }
  for (const p of probes.flatMap((q) => q.data ?? [])) {
    if (!testIds.has(p.parameterId) && !others.has(p.parameterId)) {
      others.set(p.parameterId, { id: p.parameterId, label: String(t(`controller.parameter.${p.parameterId}`)) });
    }
  }
  // A chosen value no aquarium has (any more) stays listed, so it can be removed.
  for (const id of selected) {
    if (!testIds.has(id) && !others.has(id)) others.set(id, { id, label: parameterLabel(t, id, id) });
  }
  const more = [...others.values()].sort((a, b) => a.label.localeCompare(b.label));

  async function save(next: string[] | null, kind: 'saved' | 'reset'): Promise<void> {
    setSaving(true);
    setNotice(null);
    setFailed(false);
    try {
      queryClient.setQueryData<UserPreferences>(USER_PREFERENCES_QUERY_KEY, await updateUserPreferences({ dashboardParameters: next }));
      setNotice(kind);
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }

  function toggle(id: string, checked: boolean): void {
    const next = checked ? [...selected, id] : selected.filter((p) => p !== id);
    void save(next, 'saved');
  }

  const full = selected.length >= DASHBOARD_PARAMETERS_MAX;
  const renderGroup = (titleKey: string, headingId: string, options: Option[]) => (
    <Stack gap="2">
      <Heading level={3} id={headingId}>{t(titleKey)}</Heading>
      <ul className="dashboard-cards-setting__options" aria-labelledby={headingId}>
        {options.map((o) => {
          const checked = selected.includes(o.id);
          return (
            <li key={o.id}>
              <Checkbox
                id={`dashboard-card-${o.id}`}
                label={o.label}
                checked={checked}
                // The last card cannot be removed; once the limit is reached only removing is possible.
                disabled={saving || (checked ? selected.length <= 1 : full)}
                onChange={(e) => toggle(o.id, e.target.checked)}
                aria-describedby="dashboard-cards-count"
              />
            </li>
          );
        })}
      </ul>
    </Stack>
  );

  let content;
  if (prefs.isPending) {
    content = (
      <LiveRegion busy>
        <Text size="sm" color="muted">{t('settings.dashboardCards.loading')}</Text>
      </LiveRegion>
    );
  } else if (prefs.isError) {
    content = (
      <LoadErrorState
        compact
        titleLevel={3}
        title={t('settings.dashboardCards.loadError')}
        onRetry={() => void prefs.refetch()}
        retrying={prefs.isFetching}
      />
    );
  } else {
    content = (
      <Stack gap="6">
        <Text id="dashboard-cards-count" size="sm" color="secondary">
          {t('settings.dashboardCards.count', { count: selected.length, max: DASHBOARD_PARAMETERS_MAX })}
          {full ? ` ${t('settings.dashboardCards.limitReached')}` : ''}
        </Text>
        {renderGroup('settings.dashboardCards.tests', 'dashboard-cards-tests', tests)}
        {more.length > 0 && renderGroup('settings.dashboardCards.more', 'dashboard-cards-more', more)}
        <div>
          <Button variant="secondary" onClick={() => void save(null, 'reset')} disabled={saving || prefs.data?.dashboardParameters == null}>
            {t('settings.dashboardCards.reset')}
          </Button>
        </div>
        {failed && <Alert>{t('settings.dashboardCards.saveError')}</Alert>}
        <LiveRegion visuallyHidden>
          {notice === 'saved' ? t('settings.dashboardCards.saved') : notice === 'reset' ? t('settings.dashboardCards.resetDone') : ''}
        </LiveRegion>
      </Stack>
    );
  }

  return (
    <Section
      id={DASHBOARD_CARDS_ANCHOR}
      title={t('settings.dashboardCards.title')}
      description={t('settings.dashboardCards.description', { max: DASHBOARD_PARAMETERS_MAX })}
      className={className}
    >
      {content}
    </Section>
  );
}

export default DashboardCardsSection;
